import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { useProject } from '../../utils/ProjectContext';

import '../../css/ReportsPage.css';


// import '../../css/Tooltip.css;
import SessionDetailsModal from './SessionDetails';

const ReportsPage = () => {
    const { projectId } = useParams();
    const { user } = useAuth();
    const { projectName, VISION_URL } = useProject();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [expandedSessions, setExpandedSessions] = useState(new Set());
    const [insights, setInsights] = useState(null);
    const [loadingInsights, setLoadingInsights] = useState(false);

    const toggleSession = (sessionId, event) => {
        event.stopPropagation();
        setExpandedSessions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sessionId)) {
                newSet.delete(sessionId);
            } else {
                newSet.add(sessionId);
            }
            return newSet;
        });
    };

    const formatDate = (date) => {
        const d = new Date(date);
        // Get local date components
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDefaultDateRange = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);

        return {
            startDate: formatDate(start),
            endDate: formatDate(end)
        };
    };

    const [dateRange, setDateRange] = useState(getDefaultDateRange());

    const handleSessionClick = (session) => {
        console.log('Session click:', {
            session,
            id: session._id,
            metadata: session.metadata
        });
        setSelectedSessionId(session._id);
    };

    const fetchInsights = async () => {
        if (!projectId) {
            setError('No project selected. Please select a project first.');
            return;
        }
        try {
            setLoadingInsights(true);
            const url = `https://www.cuecumber.com.au/vision_server/api/analytics/insights?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&projectId=${projectId}`;

            console.log('Fetching insights from:', url);
            const insightsResponse = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
            });

            if (!insightsResponse.ok) {
                const errorText = await insightsResponse.text();
                console.error('Insights API error:', {
                    status: insightsResponse.status,
                    statusText: insightsResponse.statusText,
                    errorText,
                });
                throw new Error(`Failed to fetch insights: ${insightsResponse.status} - ${errorText}`);
            }

            const insightsData = await insightsResponse.json();
            setInsights(insightsData);
            setError(null);
        } catch (err) {
            const errorMessage = `Failed to fetch insights: ${err.message}`;
            setError(errorMessage);
            console.error('Error fetching insights:', err);
            setInsights(null);
        } finally {
            setLoadingInsights(false);
        }
    };


    const fetchStats = useCallback(async () => {
        try {
            if (!projectId) {
                console.error('No projectId provided');
                return;
            }
            if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
                console.error('Invalid date range:', dateRange);
                return;
            }

            setLoading(true);
            // Create Date objects for start and end of day in local timezone
            const startDateTime = new Date(dateRange.startDate);
            startDateTime.setHours(0, 0, 0, 0);
            const endDateTime = new Date(dateRange.endDate);
            endDateTime.setHours(23, 59, 59, 999);

            console.log('Date range details:', {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                startLocal: startDateTime.toLocaleString(),
                endLocal: endDateTime.toLocaleString(),
                startISO: startDateTime.toISOString(),
                endISO: endDateTime.toISOString()
            });

            const url = `${VISION_URL}/api/analytics/projects/${projectId}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
            console.log('Fetching stats from:', url, {
                projectId,
                dateRange,
                currentUrl: window.location.href
            });

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error details:', {
                    status: response.status,
                    statusText: response.statusText,
                    url: url,
                    projectId: projectId,
                    dateRange: dateRange,
                    error: errorText
                });
                throw new Error(`Failed to fetch session data: ${errorText}`);
            }

            const data = await response.json();

            // Validate and sanitize the data
            const sanitizedData = {
                total_sessions: data.total_sessions || 0,
                total_duration: data.total_duration || 0,
                avg_duration: data.avg_duration || 0,
                sessions: Array.isArray(data.sessions) ? data.sessions : []
            };

            setStats(sanitizedData);
            setError(null);
        } catch (err) {
            setError(`Failed to fetch session statistics: ${err.message}`);
            console.error('Error:', err);
            setStats(null);
        } finally {
            setLoading(false);

        }
    }, [projectId, dateRange, setLoading, setError, setStats]);

    // Fetch stats when date range changes
    useEffect(() => {
        if (projectId) {
            console.log('Initializing stats fetch:', {
                projectId,
                dateRange
            });
            fetchStats();
        } else {
            console.log('No projectId available for stats fetch');
        }
    }, [dateRange, projectId, fetchStats]);

    const formatDuration = (seconds) => {
        if (typeof seconds !== 'number') return '0s';
        if (seconds < 60) return `${seconds.toFixed(2)}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds.toFixed(2)}s`;
    };

    if (loading) return (
        <div className="loading-spinner">
            <div className="spinner"></div>
        </div>
    );

    if (error) return (
        <div className="error-message">
            <h2 className="text-lg font-semibold mb-2">Error</h2>
            <p>{error}</p>
        </div>
    );

    if (!stats) return (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
            No data available
        </div>
    );

    return (
        <div className="reports-container">
            <div className="reports-header">
                <h1 className="text-2xl font-bold">Session Statistics</h1>
                <div className="reports-nav">
                    <Link to={`/sessionStats/${projectId}`} className="chart-link">
                        <i className="fas fa-chart-line"></i>
                        View Charts
                    </Link>
                </div>
            </div>

            <div className="date-controls">
                <label className="date-input">
                    Start Date:
                    <input
                        type="date"
                        className="border rounded p-1"
                        value={dateRange.startDate}
                        onChange={(e) => {
                            setDateRange(prev => ({ ...prev, startDate: e.target.value }));
                        }}
                    />
                </label>
                <label className="date-input">
                    End Date:
                    <input
                        type="date"
                        className="border rounded p-1"
                        value={dateRange.endDate}
                        onChange={(e) => {
                            setDateRange(prev => ({ ...prev, endDate: e.target.value }));
                        }}
                    />
                </label>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3 className="stat-title">Total Sessions</h3>
                    <p className="stat-value">{stats.total_sessions}</p>
                </div>
                <div className="stat-card">
                    <h3 className="stat-title">Total Duration</h3>
                    <p className="stat-value">{formatDuration(stats.total_duration)}</p>
                </div>
                <div className="stat-card">
                    <h3 className="stat-title">Average Duration</h3>
                    <p className="stat-value">{formatDuration(stats.avg_duration)}</p>
                </div>
            </div>

            <div className="insights-section">
                <h2 className="text-xl font-semibold mb-4">AI Insights</h2>
                <button
                    className={`insights-button ${loadingInsights ? 'disabled' : ''}`}
                    onClick={fetchInsights}
                    disabled={loadingInsights}
                >
                    {loadingInsights ? 'Analyzing...' : 'Analyze Sessions'}
                </button>

                {insights && (
                    <div className="insights-card">
                        <p className="insights-summary">{insights.summary}</p>
                        {insights.trends && insights.trends.length > 0 && (
                            <div className="trends-container">
                                <h3 className="trends-title">Key Trends:</h3>
                                <ul className="trends-list">
                                    {insights.trends.map((trend, index) => (
                                        <li key={index} className="trend-item">• {trend}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <p className="session-count">
                            Analysis based on {insights.sessionCount} sessions
                        </p>
                    </div>
                )}
            </div>

            <div className="sessions-list">
                <h2 className="text-xl font-semibold mb-4">
                    {(projectName || 'Project') + (projectName?.toLowerCase().endsWith('s') ? "'" : "'s")} Session Statistics
                </h2>
                <div className="session-row header">
                    <div className="session-type">Type</div>
                    <div className="session-name">Name</div>
                    <div className="session-user">User</div>
                    <div className="session-duration">Duration</div>
                    <div className="session-sampling">Sampling</div>
                    <div className="session-outcome">Outcome</div>
                    <div className="session-selection">Selection</div>
                    <div className="session-timestamp">Time</div>
                </div>
                {stats.sessions.length === 0 ? (
                    <div className="bg-gray-50 p-4 rounded-lg text-gray-600">
                        No sessions found for the selected date range
                    </div>
                ) : (
                    Object.entries(
                        stats.sessions.reduce((groups, session) => {
                            const date = new Date(session.timestamp).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            });
                            if (!groups[date]) {
                                groups[date] = [];
                            }
                            groups[date].push(session);
                            return groups;
                        }, {})
                    )
                    .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                    .map(([date, sessions]) => (
                        <div key={date} className="date-group">
                            <div className="date-header">
                                <h3>{date}</h3>
                            </div>
                            {sessions.map((session) => (
                        <div
                            key={session._id}
                            className="session-row"
                            onClick={() => handleSessionClick(session)}
                        >
                            <div className="session-type">
                                {session.metadata?.context === 'ThisThen' ? 'First/Then' : session.metadata?.context || 'Unknown'}
                            </div>
                            <div className="session-name">
                                {session.metadata?.dataName || 'Unnamed'}
                            </div>
                            <div className="session-user">
                                {session.metadata?.user?.name || session.metadata?.userName || 'Unknown'}
                            </div>
                            <div className="session-duration">
                                {formatDuration(session.metadata?.duration)}
                                <span className="metadata">
                                    {session.metadata?.fps?.toFixed(1) || 0} fps
                                </span>
                            </div>
                            <div className="session-sampling">
                                {session.metadata?.sampling?.average_rate || 0}Hz
                            </div>
                            <div className={`session-outcome ${session.metadata?.session_outcome?.parental_feedback?.type || 'neutral'}`}>
                                {session.metadata?.session_outcome?.parental_feedback?.type || 'No feedback'}
                            </div>
                            <div className="session-selection">
                                {session.metadata?.session_outcome?.final_selection?.name || '-'}
                            </div>
                            <div className="session-timestamp">
                                {new Date(session.timestamp).toLocaleString()}
                            </div>
                            <div className="session-tooltip">
                                <div className="tooltip-metadata">
                                    <div>
                                        <span>Sample Rate History:</span>
                                        Initial: {session.metadata?.sampling?.initial_rate}Hz →
                                        Final: {session.metadata?.sampling?.final_rate}Hz
                                        ({session.metadata?.sampling?.rate_changes} changes)
                                    </div>
                                    <div>
                                        <span>Face Detection:</span>
                                        {session.metadata?.viewer_detection?.stable_face_found ? 'Stable' : 'Unstable'}
                                        {session.metadata?.viewer_detection?.face_stability_score && 
                                            ` (${(session.metadata.viewer_detection.face_stability_score * 100).toFixed(1)}%)`}
                                    </div>
                                    <div>
                                        <span>Frames:</span>
                                        Analyzed: {session.metadata?.frames_analyzed || 0} / 
                                        Total: {session.metadata?.total_frames || 0}
                                    </div>
                                </div>
                                <div className="tooltip-content">
                                    {session.standard_summary?.split(/(?=#\s|##\s|\-\s)/gm).map((line, index) => {
                                        const trimmedLine = line.trim();
                                        if (trimmedLine.startsWith('# ')) {
                                            return <h1 key={index} className="tooltip-h1">{trimmedLine.substring(2).toUpperCase()}</h1>;
                                        } else if (trimmedLine.startsWith('## ')) {
                                            return <h2 key={index} className="tooltip-h2">{trimmedLine.substring(3).toUpperCase()}</h2>;
                                        } else if (trimmedLine.startsWith('- ')) {
                                            return <p key={index} className="tooltip-paragraph">{trimmedLine.substring(2)}</p>;
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                        </div>
                    ))
                )}
            </div>

            <SessionDetailsModal
                open={selectedSessionId !== null}
                onClose={() => setSelectedSessionId(null)}
                sessionId={selectedSessionId}
            />
        </div>
    );
};

export default ReportsPage;